import { useState } from 'react';
import { FileUploadSection, type UploadedFile } from '@powercore/ui-blocks';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  useToast,
} from '@powercore/ui';
import {
  Download,
  Trash2,
  Eye,
  FolderOpen,
  FileText,
  Image,
  File,
  HardDrive,
} from 'lucide-react';
import { api } from '../lib/api';

interface StoredFile {
  id: string;
  name: string;
  size: number;
  type: string;
  key: string;
  uploadedAt: string;
  status: 'complete';
}

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Number.parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function getFileIcon(type: string) {
  if (type.startsWith('image/')) return <Image className="h-5 w-5 text-green-500" />;
  if (type === 'application/pdf') return <FileText className="h-5 w-5 text-red-500" />;
  return <File className="h-5 w-5 text-gray-500" />;
}

export default function FilesPage() {
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [storedFiles, setStoredFiles] = useState<StoredFile[]>([]);
  const { addToast } = useToast();

  const handleUpload = async (files: File[]) => {
    for (const file of files) {
      // Update status to uploading
      setUploadedFiles((prev) =>
        prev.map((f) =>
          f.file === file
            ? { ...f, status: 'uploading' as const, progress: 0 }
            : f
        )
      );

      try {
        // 1. Get presigned URL from API
        const presignResult = await api.presignUpload({
          filename: file.name,
          contentType: file.type,
          entityType: 'demo',
          entityId: 'files-page',
        });

        if (presignResult.error || !presignResult.data) {
          throw new Error(presignResult.error?.message || 'Failed to get upload URL');
        }

        const { url, key } = presignResult.data;

        // 2. Upload directly to R2
        setUploadedFiles((prev) =>
          prev.map((f) => (f.file === file ? { ...f, progress: 30 } : f))
        );

        const uploadSuccess = await api.uploadFile(url, file);

        if (!uploadSuccess) {
          throw new Error('Upload to storage failed');
        }

        // 3. Mark as complete and add to stored files
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.file === file
              ? { ...f, status: 'complete' as const, progress: 100 }
              : f
          )
        );

        // Add to stored files list
        const newStoredFile: StoredFile = {
          id: key,
          name: file.name,
          size: file.size,
          type: file.type,
          key: key,
          uploadedAt: new Date().toISOString(),
          status: 'complete',
        };
        setStoredFiles((prev) => [newStoredFile, ...prev]);

        addToast({
          title: 'File uploaded',
          description: `${file.name} has been uploaded to R2.`,
          variant: 'success',
        });
      } catch (error) {
        // Mark as error
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.file === file
              ? { ...f, status: 'error' as const, error: error instanceof Error ? error.message : 'Upload failed' }
              : f
          )
        );

        addToast({
          title: 'Upload failed',
          description: error instanceof Error ? error.message : 'Failed to upload file',
          variant: 'error',
        });
      }
    }
  };

  const handleFilesChange = (files: UploadedFile[]) => {
    setUploadedFiles(files);
  };

  const handleRemoveUploaded = (fileId: string) => {
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleDownload = async (file: StoredFile) => {
    try {
      const result = await api.presignDownload({ key: file.key });
      if (result.error || !result.data) {
        throw new Error(result.error?.message || 'Failed to get download URL');
      }
      // Open download URL in new tab
      window.open(result.data.url, '_blank');
      addToast({
        title: 'Download started',
        description: `Downloading ${file.name}...`,
        variant: 'default',
      });
    } catch (error) {
      addToast({
        title: 'Download failed',
        description: error instanceof Error ? error.message : 'Failed to download file',
        variant: 'error',
      });
    }
  };

  const handleDelete = (file: StoredFile) => {
    // Remove from local state (in a real app, also delete from R2)
    setStoredFiles((prev) => prev.filter((f) => f.id !== file.id));
    addToast({
      title: 'File removed',
      description: `${file.name} has been removed from the list.`,
      variant: 'success',
    });
  };

  // Calculate storage stats
  const totalSize = storedFiles.reduce((acc, f) => acc + f.size, 0);
  const totalFiles = storedFiles.length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Files</h1>
          <p className="text-gray-500">
            Upload and manage your files with R2 storage
          </p>
        </div>
      </div>

      {/* Storage Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-mazo-50 p-3">
              <HardDrive className="h-6 w-6 text-mazo-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{formatFileSize(totalSize)}</div>
              <div className="text-sm text-gray-500">Total Storage Used</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-green-50 p-3">
              <FolderOpen className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">{totalFiles}</div>
              <div className="text-sm text-gray-500">Total Files</div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-4">
            <div className="rounded-lg bg-purple-50 p-3">
              <Image className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <div className="text-2xl font-bold">
                {storedFiles.filter((f) => f.type.startsWith('image/')).length}
              </div>
              <div className="text-sm text-gray-500">Images</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Upload Section */}
      <FileUploadSection
        title="Upload New Files"
        description="Drag and drop files here, or click to select. Files are uploaded to Cloudflare R2."
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
        multiple={true}
        maxSize={50 * 1024 * 1024} // 50MB
        maxFiles={10}
        files={uploadedFiles}
        onFilesChange={handleFilesChange}
        onUpload={handleUpload}
        onRemove={handleRemoveUploaded}
      />

      {/* Stored Files */}
      <Card>
        <CardHeader>
          <CardTitle>Your Files</CardTitle>
          <CardDescription>
            Files stored in your R2 bucket. Click to view or download.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {storedFiles.length === 0 ? (
            <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center">
              <FolderOpen className="mx-auto h-12 w-12 text-gray-400" />
              <p className="mt-2 text-gray-600">No files uploaded yet</p>
              <p className="text-sm text-gray-500">
                Upload files using the section above
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Size</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Uploaded</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {storedFiles.map((file) => (
                  <TableRow key={file.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {getFileIcon(file.type)}
                        <span className="font-medium">{file.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>{formatFileSize(file.size)}</TableCell>
                    <TableCell>
                      <Badge variant="default">
                        {file.type.split('/')[1]?.toUpperCase() || 'FILE'}
                      </Badge>
                    </TableCell>
                    <TableCell>{formatDate(file.uploadedAt)}</TableCell>
                    <TableCell>
                      <Badge variant="success">Complete</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDownload(file)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDownload(file)}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(file)}
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* R2 Integration Info */}
      <Card>
        <CardHeader>
          <CardTitle>R2 Storage Integration</CardTitle>
          <CardDescription>
            This demo shows how files are managed with Cloudflare R2
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4 text-sm">
            <div className="rounded-lg bg-mazo-50 p-4">
              <h4 className="font-medium text-mazo-900">How it works:</h4>
              <ol className="mt-2 list-inside list-decimal space-y-1 text-mazo-800">
                <li>Frontend requests a presigned upload URL from the API</li>
                <li>File is uploaded directly to R2 using the presigned URL</li>
                <li>API stores file metadata in Supabase</li>
                <li>Downloads use presigned URLs with short expiry times</li>
              </ol>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <h4 className="font-medium">Upload Endpoint</h4>
                <code className="text-xs text-gray-600">POST /api/files/presign-upload</code>
              </div>
              <div>
                <h4 className="font-medium">Download Endpoint</h4>
                <code className="text-xs text-gray-600">GET /api/files/presign-download/:key</code>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
